import dayjs from 'dayjs';
import { z } from 'zod';
import { JA_JP } from '@/components/organisms/RA2NDatePicker/constants';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import { dateValueToIso } from '@/utils/dateConverter';

export const AVAILABLE_TO_INDEFINITE = '2100-12-31';
const dayPickerFormat = parseFormat(JA_JP.format);

export const singleServiceScheduleSchema = z.object({
  am_time_start: z.string().nullable().optional(),
  am_time_end: z.string().nullable().optional(),
  pm_time_start: z.string().nullable().optional(),
  pm_time_end: z.string().nullable().optional(),
  is_am_monday: z.boolean().optional(),
  is_am_tuesday: z.boolean().optional(),
  is_am_wednesday: z.boolean().optional(),
  is_am_thursday: z.boolean().optional(),
  is_am_friday: z.boolean().optional(),
  is_am_saturday: z.boolean().optional(),
  is_am_sunday: z.boolean().optional(),
  is_pm_monday: z.boolean().optional(),
  is_pm_tuesday: z.boolean().optional(),
  is_pm_wednesday: z.boolean().optional(),
  is_pm_thursday: z.boolean().optional(),
  is_pm_friday: z.boolean().optional(),
  is_pm_saturday: z.boolean().optional(),
  is_pm_sunday: z.boolean().optional(),
  available_from: z.string().nullable().optional(),
  available_to: z.string().nullable().optional(),
});

/** Shared remarks for both A2 and R2 (not per-service) */
const sharedRemarksSchema = z
  .string()
  .trim()
  .max(255, '注意事項は255文字以下にしてください。')
  .nullable()
  .optional();

export const clinicLocationMassagerScheduleSchema = z.object({
  r2: singleServiceScheduleSchema,
  a2: singleServiceScheduleSchema,
  remarks: sharedRemarksSchema,
});

export type ClinicLocationMassagerScheduleFormData = z.infer<
  typeof clinicLocationMassagerScheduleSchema
>;

/**
 * Returns a refined schema based on dynamic validation parameters.
 */
export const getClinicLocationMassagerScheduleSchema = (params: {
  availableTo?: string | null;
  r2LastVisitDate?: string | null;
  a2LastVisitDate?: string | null;
  r2Enabled?: boolean;
  a2Enabled?: boolean;
}) => {
  const { availableTo, r2LastVisitDate, a2LastVisitDate, r2Enabled, a2Enabled } = params;

  const validateService = (
    data: z.infer<typeof singleServiceScheduleSchema>,
    ctx: z.RefinementCtx,
    pathPrefix: string[],
    lastVisitDate?: string | null,
    enabled?: boolean
  ) => {
    if (!enabled) {
      return;
    }

    // 適用開始日必須
    if (data.available_from == null || String(data.available_from).trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '適用開始日は必須です。',
        path: [...pathPrefix, 'available_from'],
      });
    }

    // 適用終了日 >= 適用開始日
    if (data.available_from && data.available_to) {
      const fromDate = dayjs(parseDate(data.available_from, dayPickerFormat));
      const toDate = dayjs(parseDate(data.available_to, dayPickerFormat));
      if (fromDate.isValid() && toDate.isValid() && toDate.isBefore(fromDate, 'day')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '適用終了日は、適用開始日以降の日付を入力してください。',
          path: [...pathPrefix, 'available_to'],
        });
      }
    }

    // 拠点の適用期間チェック
    if (availableTo && availableTo !== AVAILABLE_TO_INDEFINITE && data.available_to) {
      const selectedIso = dateValueToIso(String(data.available_to));
      if (selectedIso && selectedIso > availableTo.slice(0, 10)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '適用終了日は、拠点の適用期間内の日付を入力してください。',
          path: [...pathPrefix, 'available_to'],
        });
      }
    }

    // 最終訪問日チェック
    if (lastVisitDate && data.available_to) {
      const selectedIso = dateValueToIso(String(data.available_to));
      if (selectedIso && selectedIso < lastVisitDate.slice(0, 10)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'すでに設定済みの期間で訪問入力されています。編集が必要な場合は、対象日付の訪問データを削除してから再度行ってください。',
          path: [...pathPrefix, 'available_to'],
        });
      }
    }
  };

  return clinicLocationMassagerScheduleSchema.superRefine((data, ctx) => {
    validateService(data.r2, ctx, ['r2'], r2LastVisitDate, r2Enabled);
    validateService(data.a2, ctx, ['a2'], a2LastVisitDate, a2Enabled);
  });
};
